import type { ProjectGraphEdge, ProjectGraphResponse } from '@avisoma/shared';
import type { CsvColumn } from '../../components/csv';
import { indexOf, pairKey } from './graphIndex';
import type { VisibleGraph } from './graphModel';
import { linkedPageIds } from './mentionEvidence';
import { nodeLabel } from './nodeLabel';

/** One relationship of the view, as a CSV row. */
export type RelationshipRow = {
  from_kind: string;
  from_label: string;
  from_id: string;
  kind: string;
  role: string;
  /** A dated edge's own date (`YYYY-MM-DD`); empty for an undated edge. */
  date: string;
  to_kind: string;
  to_label: string;
  to_id: string;
  /** The edge's sources: the documents that record it. */
  documents: number;
  linked_pages: number;
};

const KEYS: (keyof RelationshipRow)[] = [
  'from_kind',
  'from_label',
  'from_id',
  'kind',
  'role',
  'date',
  'to_kind',
  'to_label',
  'to_id',
  'documents',
  'linked_pages',
];
export const RELATIONSHIP_COLUMNS: CsvColumn[] = KEYS.map((key) => ({ accessor: key, label: key }));

const edgeMaps = new WeakMap<ProjectGraphResponse, ReadonlyMap<string, ProjectGraphEdge>>();
/** The graph's edges by id, built once per graph snapshot. */
function edgesByIdOf(graph: ProjectGraphResponse): ReadonlyMap<string, ProjectGraphEdge> {
  const known = edgeMaps.get(graph);
  if (known) return known;
  const map = new Map(graph.edges.map((e) => [e.id, e]));
  edgeMaps.set(graph, map);
  return map;
}

/**
 * A row per relationship the view shows, in the view's edge order, each once. A real edge is its own row. A member
 * shown under an expanded group hangs from it by a synthetic edge, which stands for the real edge between the
 * group's parent and the member: that real edge is the row. Other synthetic edges (into a group, from the project
 * root) are no relationship of the data, so they and the group nodes never appear. An IPC position and every edge
 * that touches one (`instance-of`, DR-4818; `previously-at`, DR-5200; `interfaces` / `kit`, DR-5201) never appear
 * either: the catalogue is licensed content and stays out of downloaded files.
 */
export function visibleRelationshipRows(
  graph: ProjectGraphResponse,
  visible: Pick<VisibleGraph, 'nodes' | 'edges'>,
): RelationshipRow[] {
  const { nodesById, edgeOfPair } = indexOf(graph);
  const edgesById = edgesByIdOf(graph);
  const parentOfGroup = new Map(visible.nodes.flatMap((n) => (n.kind === 'group' ? [[n.id, n.parentId]] : [])));
  const realEdgeOf = (viewEdge: VisibleGraph['edges'][number]) => {
    if (!viewEdge.synthetic) return edgesById.get(viewEdge.id);
    const parentId = parentOfGroup.get(viewEdge.from);
    return parentId === undefined ? undefined : edgeOfPair.get(pairKey(parentId, viewEdge.to));
  };
  const seen = new Set<string>();
  return visible.edges.flatMap((viewEdge) => {
    const edge = realEdgeOf(viewEdge);
    const from = edge && nodesById.get(edge.from);
    const to = edge && nodesById.get(edge.to);
    if (!edge || !from || !to || seen.has(edge.id)) return [];
    if (edge.kind === 'instance-of' || from.kind === 'ipc' || to.kind === 'ipc') return [];
    seen.add(edge.id);
    return [
      {
        from_kind: from.kind,
        from_label: nodeLabel(from),
        from_id: from.id,
        kind: edge.kind,
        role: edge.role ?? '',
        date: edge.date ?? '',
        to_kind: to.kind,
        to_label: nodeLabel(to),
        to_id: to.id,
        documents: edge.sources.length,
        linked_pages: linkedPageIds(edge).length,
      },
    ];
  });
}

const pad = (n: number) => String(n).padStart(2, '0');

/** `project-graph-<first 8 of the project id>-<yyyymmdd>.csv`, dated in local time. */
export function relationshipsFileName(projectId: string, date: Date): string {
  const day = `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}`;
  return `project-graph-${projectId.slice(0, 8)}-${day}.csv`;
}
